// src/components/auth/MfaSettingsModal.tsx
// Enable/disable TOTP-based MFA (Google Authenticator/Authy compatible).
import { useState } from 'react';
import { ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from 'services/authService';
import { showToast } from 'store/slices/toastSlice';
import FieldError from 'components/ui/FieldError';

type Step = 'status' | 'setup-qr' | 'setup-confirm' | 'backup-codes' | 'disable';

export default function MfaSettingsModal({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('status');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [apiError, setApiError] = useState('');
  const [confirmAttempted, setConfirmAttempted] = useState(false);
  const [disableAttempted, setDisableAttempted] = useState(false);

  const confirmErrors: { confirmCode?: string } = {};
  if (confirmCode.trim().length !== 6) confirmErrors.confirmCode = 'Enter the 6-digit code';

  const disableErrors: { disablePassword?: string; disableCode?: string } = {};
  if (!disablePassword) disableErrors.disablePassword = 'Password is required';
  if (!disableCode.trim()) disableErrors.disableCode = 'Authenticator code is required';

  const { data: status } = useQuery({ queryKey: ['mfa', 'status'], queryFn: authService.mfaStatus });

  const startSetup = useMutation({
    mutationFn: authService.mfaSetup,
    onSuccess: (res) => { setQrCode(res.qrCode); setSecret(res.secret); setStep('setup-qr'); },
    onError: (err: any) => setApiError(err?.response?.data?.message || 'Failed to start MFA setup'),
  });

  const confirmSetup = useMutation({
    mutationFn: () => authService.mfaVerifySetup(confirmCode.trim()),
    onSuccess: (res) => {
      setBackupCodes(res.backupCodes);
      setStep('backup-codes');
      queryClient.invalidateQueries({ queryKey: ['mfa', 'status'] });
    },
    onError: (err: any) => setApiError(err?.response?.data?.message || 'Invalid code'),
  });

  const disable = useMutation({
    mutationFn: () => authService.mfaDisable(disablePassword, disableCode.trim()),
    onSuccess: () => {
      dispatch(showToast('🔓 MFA disabled'));
      queryClient.invalidateQueries({ queryKey: ['mfa', 'status'] });
      setStep('status');
      setDisablePassword(''); setDisableCode('');
    },
    onError: (err: any) => setApiError(err?.response?.data?.message || 'Failed to disable MFA'),
  });

  const inp = (hasError?: boolean) =>
    `w-full px-3 py-2 bg-[var(--om-panel)] border rounded-lg ` +
    `text-[var(--om-text)] text-xs outline-none focus:border-blue-500 placeholder:text-[var(--om-text-dim)] ${
      hasError ? 'border-red-500' : 'border-[var(--om-border)]'
    }`;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[90]" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100]
                      w-full max-w-sm bg-[var(--om-panel)] border border-[var(--om-border)] rounded-2xl
                      p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="inline-flex items-center gap-1.5 text-sm font-black text-[var(--om-text)]"><ShieldCheck className="w-4 h-4" strokeWidth={2} /> Two-Factor Authentication</h2>
          <button onClick={onClose} className="text-[var(--om-text-dim)] hover:text-[var(--om-text)] text-lg leading-none">×</button>
        </div>

        {apiError && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-red-400 text-[11px] mb-3">
            {apiError}
          </div>
        )}

        {step === 'status' && (
          <div className="space-y-3">
            <p className="text-[12px] text-[var(--om-text-muted)]">
              {status?.enabled
                ? 'MFA is currently enabled on your account. Every login requires a code from your authenticator app.'
                : 'Add an extra layer of security — after your password, you\'ll also need a code from an authenticator app (Google Authenticator, Authy, etc.) to sign in.'}
            </p>
            {status?.enabled ? (
              <button onClick={() => { setApiError(''); setDisableAttempted(false); setStep('disable'); }}
                className="w-full py-2 bg-red-600/80 hover:bg-red-500 text-white rounded-xl font-bold text-xs transition-all">
                Disable MFA
              </button>
            ) : (
              <button onClick={() => { setApiError(''); startSetup.mutate(); }} disabled={startSetup.isPending}
                className="w-full py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-[var(--om-border)] text-white rounded-xl font-bold text-xs transition-all">
                {startSetup.isPending ? 'Starting...' : 'Enable MFA'}
              </button>
            )}
          </div>
        )}

        {step === 'setup-qr' && (
          <div className="space-y-3">
            <p className="text-[12px] text-[var(--om-text-muted)]">Scan this QR code with Google Authenticator, Authy, or any TOTP app:</p>
            <div className="bg-white rounded-xl p-3 flex items-center justify-center">
              <img src={qrCode} alt="MFA QR code" className="w-40 h-40" />
            </div>
            <p className="text-[11px] text-[var(--om-text-dim)] text-center">Can't scan? Enter this key manually: <span className="font-mono text-[var(--om-text-muted)] break-all">{secret}</span></p>
            <button onClick={() => { setConfirmAttempted(false); setStep('setup-confirm'); }}
              className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-xs transition-all">
              Next: Enter Code
            </button>
          </div>
        )}

        {step === 'setup-confirm' && (
          <div className="space-y-3">
            <label className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-widest block mb-1">
              Enter the 6-digit code from your app
            </label>
            <input value={confirmCode} onChange={e => setConfirmCode(e.target.value)} placeholder="123456"
              className={`${inp(confirmAttempted && !!confirmErrors.confirmCode)} text-center tracking-[0.3em] font-mono`} autoFocus />
            <FieldError message={confirmAttempted ? confirmErrors.confirmCode : undefined} />
            <button
              onClick={() => { setConfirmAttempted(true); setApiError(''); if (Object.keys(confirmErrors).length === 0) confirmSetup.mutate(); }}
              disabled={confirmSetup.isPending}
              className="w-full py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-[var(--om-border)] text-white rounded-xl font-bold text-xs transition-all">
              {confirmSetup.isPending ? 'Verifying...' : 'Confirm & Enable'}
            </button>
          </div>
        )}

        {step === 'backup-codes' && (
          <div className="space-y-3">
            <div className="flex items-start gap-1.5 bg-amber-500/10 border border-amber-500/25 rounded-xl px-3 py-2 text-amber-400 text-[11px]">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <span>MFA enabled. Save these one-time backup codes somewhere safe — each can be used once if you lose access to your authenticator app. They won't be shown again.</span>
            </div>
            <div className="bg-[var(--om-card)] border border-[var(--om-border)] rounded-lg p-3 grid grid-cols-2 gap-2 font-mono text-[12px] text-[var(--om-text)]">
              {backupCodes.map(c => <span key={c}>{c}</span>)}
            </div>
            <button onClick={onClose} className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-xs transition-all">
              Done
            </button>
          </div>
        )}

        {step === 'disable' && (
          <div className="space-y-3">
            <label className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-widest block mb-1">Password</label>
            <input value={disablePassword} onChange={e => setDisablePassword(e.target.value)} type="password" placeholder="••••••••" className={inp(disableAttempted && !!disableErrors.disablePassword)} />
            <FieldError message={disableAttempted ? disableErrors.disablePassword : undefined} />
            <label className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-widest block mb-1">Authenticator Code</label>
            <input value={disableCode} onChange={e => setDisableCode(e.target.value)} placeholder="123456" className={`${inp(disableAttempted && !!disableErrors.disableCode)} text-center tracking-[0.3em] font-mono`} />
            <FieldError message={disableAttempted ? disableErrors.disableCode : undefined} />
            <div className="flex gap-2 pt-1">
              <button onClick={() => setStep('status')} className="flex-1 py-2 bg-[var(--om-card)] border border-[var(--om-border)] rounded-xl text-[var(--om-text-muted)] text-xs hover:text-[var(--om-text)] transition-all">
                Cancel
              </button>
              <button
                onClick={() => { setDisableAttempted(true); setApiError(''); if (Object.keys(disableErrors).length === 0) disable.mutate(); }}
                disabled={disable.isPending}
                className="flex-1 py-2 bg-red-600/80 hover:bg-red-500 disabled:bg-[var(--om-border)] text-white rounded-xl font-bold text-xs transition-all">
                {disable.isPending ? 'Disabling...' : 'Disable MFA'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
